import React, { useState, useRef } from 'react';
import SVG from 'react-inlinesvg';
import styles from './Header.module.scss';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useSearchBarVisibility } from '@/admin/hooks/useSearchBarVisibility';
import AppIcon from '@/components/GenericTable/Icon/Icon';
import clsx from 'clsx';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { useDrawer } from '@/admin/context/DrawerContext';
import commonUtils from '@/utils/common';
import UserDropdown from '@/components/school-management/UserDropdown/UserDropdown';
import NotificationsBadge from '@/components/Notifications/NotificationsBadge/NotificationsBadge';

interface HeaderProps {
  className?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  onOpenSettings: () => void;
}

const Header: React.FC<HeaderProps> = ({
  className,
  isCollapsed,
  onToggleCollapse,
  onOpenSettings,
}) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const drawer = useDrawer();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);

  const toggleCollapse = () => {
    onToggleCollapse?.(!isCollapsed);
  };
  const isSearchVisible = useSearchBarVisibility();

  const firstName = user?.metadata?.firstName ?? '';
  const lastName = user?.metadata?.lastName ?? '';
  const avatarName = user?.metadata?.userSettings?.avatar?.avatarName;
  const avatarColor = (user?.metadata?.userSettings?.avatar as any)?.avatarColor ?? '';
  const avatarImg = avatarName ? commonUtils.getAvatarImage(avatarName) : null;
  const avatarInitials = [firstName, lastName]
    .filter(Boolean)
    .map(name => name[0])
    .join('');

  return (
    <header
      className={clsx(styles.header, className, {
        [styles.searchHidden]: !isSearchVisible,
      })}
    >
      <button
        className={clsx(styles.collapseButton, { [styles.collapsed]: isCollapsed })}
        onClick={toggleCollapse}
        aria-label={isCollapsed ? t('expandSidebar') || '' : t('collapseSidebar') || ''}
      >
        <AppIcon icon='leftArrow' className={clsx(styles.collapseIcon)} />
      </button>
      <div className={styles.headerContent}>
        <div
          className={clsx(styles.centerSection, {
            [styles.hidden]: !isSearchVisible,
          })}
        >
          <GlobalSearch placeholder={t('common.search') || ''} />
        </div>

        <div className={styles.rightSection}>
          <NotificationsBadge />

          <button
            ref={avatarButtonRef}
            className={styles.avatarButton}
            onClick={() => setIsDropdownOpen(prev => !prev)}
            aria-label='Open user menu'
            aria-haspopup='true'
            aria-expanded={isDropdownOpen}
          >
            <div
              className={styles.avatarCircle}
              style={{ backgroundColor: avatarColor || undefined }}
            >
              {avatarImg ? (
                <SVG src={avatarImg} className={styles.avatarSvg} />
              ) : (
                <span className={styles.avatarInitials}>{avatarInitials}</span>
              )}
            </div>
          </button>

          <div className={styles.dropdownWrapper}>
            <UserDropdown
              isOpen={isDropdownOpen}
              onOpenSettings={onOpenSettings}
              onContactUs={drawer.openContactUsDrawer}
              onClose={() => setIsDropdownOpen(false)}
              anchorRef={avatarButtonRef}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
